import React, { FC, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import { AsideLoader } from "Components/common/Loader";
import {
  getIsMissionPlansLoadedForCurrentSemanticMap,
  getMissionsPlansByCurrentSemanticMap,
} from "Slices/missionPlans/selectors";
import {
  fetchDeleteMissionPlan,
  fetchDeleteMissionVersion,
} from "Slices/missionPlans";
import { MISSION_DEPLOYMENT_ACTIONS } from "Utils/constants";
import { useMissionPlansLoader } from "Utils/loaders";
import { FMSLogger } from "FMSLogger";
import { getDiffTime } from "Utils";
import { sanitizeSearchParams } from "Utils/navigationValidation";

import { EmptyCover } from "../EmptyCover";
import { MissionItem } from "../MissionItem";
import { MissionListContentStyled, MissionListScrollArea } from "../../styles";
import { getCurrentSemanticMapId } from "Slices/appState";
import { MissionPlansLoadingError } from "../MissionPlansLoadingError";
import { MissionListActions } from "../MissionListActions/MissionListActions";
import { MissionListFooter } from "../MissionListFooter/MissionListFooter";
import { ADD_MISSION_MODAL, toggleModal } from "Slices/modals";

const missionDeploymentActionsLogger = FMSLogger.byPrefix(
  MISSION_DEPLOYMENT_ACTIONS
);

type MissionListProps = {
  isShowAssignPicker: boolean;
  setActiveTab: (activeTab: string) => void;
  cancelAction: () => void;
  setEditData: () => void;
  deployAction: (missionAsset: MissionAsset) => void;
  deployPlanMissionAction: (data: any) => void;
  savedMissionsDisabled: boolean;
  previousMode?: string;
};

// Utility to check if a value is a valid timestamp (string | number | Date)
export function isValidTimestamp(d: unknown): d is string | number | Date {
  if (!d) return false;
  const date =
    typeof d === "string" || typeof d === "number" || d instanceof Date
      ? new Date(d)
      : null;
  return !!date && !isNaN(date.getTime());
}

// Helper to safely get time in ms from a timestamp
export function getTimeSafe(d: unknown): number {
  return isValidTimestamp(d)
    ? new Date(d as string | number | Date).getTime()
    : 0;
}

// Add lastDeployed (string) and lastDeployedMs (number) to each mission plan
export function addLastDeployedToMissionPlans(missionPlans: any[]): any[] {
  return missionPlans.map((missionPlan) => {
    // Use last_deployed_any_version if available (shows deployment across all versions)
    // Fall back to timeStamp for legacy missions
    // Handle both snake_case and camelCase from API
    const deploymentTime =
      missionPlan.last_deployed_any_version ||
      missionPlan.lastDeployedAnyVersion ||
      missionPlan.timeStamp;

    const valid = isValidTimestamp(deploymentTime);
    const lastDeployedMs = valid
      ? Date.now() - getTimeSafe(deploymentTime)
      : null;
    return {
      ...missionPlan,
      lastDeployed: valid ? getDiffTime(deploymentTime as string) : null,
      lastDeployedMs,
    };
  });
}

// Filter mission plans by search term
export function filterMissionPlansBySearch(
  missionPlans: any[],
  search: string
): any[] {
  if (!search) return missionPlans;
  return missionPlans.filter((missionPlan) =>
    missionPlan.name?.toLowerCase().includes(search.toLowerCase())
  );
}

// Sort mission plans by different criteria
export function sortMissionPlans(
  missionPlans: any[],
  sortValue: string
): any[] {
  const plans = [...missionPlans];

  if (sortValue === "updatedAt") {
    // For 'Last Updated', sort by updatedAt field (most recent first)
    return plans.sort((a, b) => {
      const dateA = new Date(a.updatedAt).getTime();
      const dateB = new Date(b.updatedAt).getTime();
      return dateB - dateA;
    });
  }

  if (sortValue === "lastDeployed") {
    // For 'Last Deployed', never-deployed missions sort to the bottom
    return plans.sort((a, b) => {
      const aValid = typeof a.lastDeployedMs === "number";
      const bValid = typeof b.lastDeployedMs === "number";
      if (!aValid && !bValid) return 0;
      if (!aValid) return 1;
      if (!bValid) return -1;

      // most recently deployed first (smaller ms = more recent)
      return a.lastDeployedMs - b.lastDeployedMs;
    });
  }

  return plans;
}

// Log mission deployment actions
export function logMissionDeploymentAction(action: string): void {
  missionDeploymentActionsLogger.debug(action);
}

// Create mission data for MissionItem component
export function createMissionData(missionPlan: any): any {
  return {
    ...missionPlan,
    data: missionPlan.stops || [],
  };
}

// Compute the next expandedMissionId given the current value and the clicked id.
// Returns the clicked id to open it, or null if the same id is clicked again (toggle close).
export function getNextExpandedMissionId(
  currentExpandedId: string | null,
  clickedId: string
): string | null {
  return currentExpandedId === clickedId ? null : clickedId;
}

export const MissionList: FC<MissionListProps> = memo(
  ({
    isShowAssignPicker,
    setActiveTab,
    cancelAction,
    setEditData,
    deployAction,
    deployPlanMissionAction,
    savedMissionsDisabled,
    previousMode,
  }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const missionPlansForCurrentSemanticMap = useSelector(
      getMissionsPlansByCurrentSemanticMap
    );
    const isMissionPlansLoadedForCurrentSemanticMap = useSelector(
      getIsMissionPlansLoadedForCurrentSemanticMap
    );
    const deletePlanMissionAction = (id: string) =>
      dispatch(fetchDeleteMissionPlan(id));

    const deleteVersionAction = React.useCallback(
      (data: { missionId: string; versionNumber: number }) => {
        dispatch(fetchDeleteMissionVersion(data));
      },
      [dispatch]
    );

    const currentSemanticMapId = useSelector(getCurrentSemanticMapId);

    const { isMissionPlansLoading, isMissionPlansLoadingError } =
      useMissionPlansLoader(currentSemanticMapId);

    // Add lastDeployed (string) and lastDeployedMs (number) to each mission plan
    const missionPlansWithLastDeployed = React.useMemo(() => {
      return addLastDeployedToMissionPlans(missionPlansForCurrentSemanticMap);
    }, [missionPlansForCurrentSemanticMap]);

    // Local state for search and sort
    const [search, setSearch] = React.useState("");
    const [sortValue, setSortValue] = React.useState("updatedAt");

    // Single-expand accordion: only one MissionItem can be open at a time
    const [expandedMissionId, setExpandedMissionId] = React.useState<
      string | null
    >(null);

    const handleMissionExpand = React.useCallback((missionId: string) => {
      setExpandedMissionId((prev) => getNextExpandedMissionId(prev, missionId));
    }, []);

    // Local filter by mission name
    const filteredMissionPlans = React.useMemo(() => {
      return filterMissionPlansBySearch(missionPlansWithLastDeployed, search);
    }, [missionPlansWithLastDeployed, search]);

    // Local sort logic
    const sortedMissionPlans = React.useMemo(() => {
      return sortMissionPlans(filteredMissionPlans, sortValue);
    }, [filteredMissionPlans, sortValue]);

    return (
      <MissionListContentStyled isShowAssignPicker={isShowAssignPicker}>
        {isMissionPlansLoading && <AsideLoader />}
        {isMissionPlansLoadingError && <MissionPlansLoadingError />}
        {isMissionPlansLoadedForCurrentSemanticMap && (
          <>
            <MissionListScrollArea>
              {missionPlansForCurrentSemanticMap.length === 0 && (
                <EmptyCover
                  btnClick={() => {
                    logMissionDeploymentAction("new mission from empty cover");
                    // Navigate away from /missions/list to prevent modal re-opening
                    if (location.pathname === "/missions/list") {
                      navigate(
                        `/missions${sanitizeSearchParams(location.search)}`,
                        { replace: true }
                      );
                    }
                    dispatch(toggleModal({ type: ADD_MISSION_MODAL }));
                    // Only change tab if setActiveTab is a function (not in modal context)
                    if (typeof setActiveTab === "function") {
                      setActiveTab("newMission");
                    }
                  }}
                />
              )}
              {missionPlansForCurrentSemanticMap.length > 0 && (
                <MissionListActions
                  search={search}
                  setSearch={setSearch}
                  sortValue={sortValue}
                  setSortValue={setSortValue}
                />
              )}
              {sortedMissionPlans.map((missionPlan, index) => (
                <MissionItem
                  key={missionPlan.id}
                  missionData={createMissionData(missionPlan)}
                  currentIndex={index}
                  setEditData={setEditData}
                  deletePlanMissionAction={deletePlanMissionAction}
                  onDeleteVersion={deleteVersionAction}
                  savedMissionsDisabled={savedMissionsDisabled}
                  deployAction={() => deployAction(missionPlan)}
                  deployPlanMissionAction={deployPlanMissionAction}
                  previousMode={previousMode}
                  isOpen={expandedMissionId === missionPlan.id}
                  onExpand={handleMissionExpand}
                />
              ))}
            </MissionListScrollArea>
            <MissionListFooter
              onGoBack={() => {
                logMissionDeploymentAction("cancel on mission list");
                cancelAction();
              }}
              onCreateMission={() => {
                logMissionDeploymentAction("create new mission from footer");
                // Navigate away from /missions/list to prevent modal re-opening
                if (location.pathname === "/missions/list") {
                  navigate(
                    `/missions${sanitizeSearchParams(location.search)}`,
                    { replace: true }
                  );
                }
                dispatch(toggleModal({ type: ADD_MISSION_MODAL }));
                // Only change tab if setActiveTab is a function (not in modal context)
                if (typeof setActiveTab === "function") {
                  setActiveTab("newMission");
                }
              }}
            />
          </>
        )}
      </MissionListContentStyled>
    );
  }
);
